import { useState, useEffect, useRef } from 'react';
import avatar from './../assets/images/pngegg.png';
import search from './../assets/images/search.png';
import { PreEmptedSpot } from './types';
import { formatUTCToTimestamp } from '../utils/convertUTCtoTime';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useMakeFoodRecommendation } from '../context/MakeGoodContext';
import { deleteRequest, getRequest } from '../common/HTTPService';

const PreEmptedSpotList = ({
  fetchMakeGoodRecommendations,
}: {
  fetchMakeGoodRecommendations: (preEmptedSpot: PreEmptedSpot) => Promise<void>;
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { preEmptedSpotList, setPreEmptedSpotList, setPreEmptedSpotDetails } =
    useMakeFoodRecommendation();

  const dropdownRef = useRef<HTMLDivElement>(null); // Ref for dropdown element

  const handleItemClick = (preEmptedSpot: PreEmptedSpot) => {
    setActiveItemId(preEmptedSpot.preempted_spots_table_id);
    fetchMakeGoodRecommendations(preEmptedSpot);
  };

  const handleClearInbox = async () => {
    try {
      await deleteRequest('/api/v1/database-records');
      setPreEmptedSpotDetails(null);
      try {
        let result = await getRequest('/api/v1/preempted-spots');
        setPreEmptedSpotList(result);
      } catch (e) {
        setPreEmptedSpotList([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <section className="w-1/3 bg-white border-bordergrey border-r flex flex-col h-full">
      {/* Header with Inbox and MoreVertIcon */}
      <div className="relative flex justify-between items-center mt-7 ml-7 mr-7">
        <h1 className="font-bold text-2xl">Inbox</h1>

        {/* MoreVertIcon with dropdown */}
        <div className="relative" ref={dropdownRef}>
          <MoreVertIcon
            className="text-gray-600 cursor-pointer"
            onClick={toggleDropdown} // Handle click to toggle dropdown
          />

          {isDropdownOpen && (
            <div className="absolute right-0 w-30 bg-white border border-gray-200 shadow-lg rounded-[5px] z-10">
              <ul className="py-1">
                <li
                  className="px-4 py-1 text-sm text-gray-700  hover:bg-gray-100 cursor-pointer rounded-md transition-colors duration-300"
                  onClick={handleClearInbox}
                >
                  Clear Inbox
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="flex border border-input-grey rounded-lg p-2 ml-4 mr-4 mt-9 bg-input-grey items-center">
        <img
          src={search}
          alt="Search Icon"
          className="h-5 w-5 text-gray-400 mr-2"
        />
        <input
          className="bg-transparent focus:outline-none text-gray-600 placeholder-gray-400 w-full"
          type="text"
          placeholder="Search"
        />
      </div>
      <div className="flex justify-start pl-5 mt-4 border-b-[0.5px] border-bordergrey">
        <button
          className={`text-tab-text ${
            activeTab === 'all' ? 'border-b-4 border-tab-grey' : 'text-tab-text'
          }`}
          onClick={() => setActiveTab('all')}
        >
          All messages
        </button>
      </div>
      {preEmptedSpotList && preEmptedSpotList.length ? (
        <ul className="overflow-y-auto flex-grow">
          {preEmptedSpotList.map((preEmptedSpot: PreEmptedSpot) => (
            <li
              key={preEmptedSpot.preempted_spots_table_id}
              className={`py-4 px-6 cursor-pointer flex flex-col space-y-2 border-b-1 border-bordergrey ${
                activeItemId === preEmptedSpot.preempted_spots_table_id
                  ? 'bg-[#EBF2FA]'
                  : 'bg-white hover:bg-gray-50'
              }`}
              onClick={() => handleItemClick(preEmptedSpot)}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4 w-full">
                  <img
                    src={avatar}
                    alt={`${preEmptedSpot.sender} avatar`}
                    className="h-9 w-9 rounded-full"
                  />
                  <div className="flex flex-col w-full">
                    <div className="flex justify-between gap-2">
                      <p className="font-semibold text-[14px] text-tab-text">
                        {preEmptedSpot.sender}
                      </p>
                      <p className="text-[11px] font-normal text-right ">
                        {preEmptedSpot?.timestamp
                          ? formatUTCToTimestamp(
                              preEmptedSpot?.timestamp
                            ).includes('Yesterday')
                            ? 'Yesterday'
                            : formatUTCToTimestamp(preEmptedSpot?.timestamp)
                          : null}
                      </p>
                    </div>
                    <p className="text-[11px] mt-1 text-tab-text">
                      {`${preEmptedSpot.subject}: ${
                        preEmptedSpot.order_line_spot_number
                          ? preEmptedSpot.order_line_spot_number
                          : ''
                      }`}
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-tab-grey text-[11px]">
                {preEmptedSpot.description}
              </p>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default PreEmptedSpotList;
